import Config from '@config';
import { normalizeInfiniteQueryData } from '@gasbi/shared/common/features/utils';
import { createApi } from '@reduxjs/toolkit/query/react';

import { CACHE_EXPIRATION_TIME } from '@/features/constants';
import { Organization } from '@/features/organizations/types';
import { FiltersGrid, Pagination } from '@/features/types';
import axiosBaseQuery from '@/utils/axiosBaseQuery';

import { infiniteQueryOptions, infiniteQueryOptionsFn } from '../utils';
import {
  WorkingPeriod,
  WorkingPeriodEmployee,
  WorkingPeriodForMainOrganization,
  WorkingPeriodGrid,
  WorkingPeriodRequestBody,
  WorkingPeriodSubsystem,
} from './types';

const workingPeriodsApi = createApi({
  reducerPath: 'workingPeriodsApi',
  baseQuery: axiosBaseQuery({
    baseUrl: `${Config.VITE_API_URL}/working-periods`,
  }),
  tagTypes: [
    'WorkingPeriod',
    'WorkingPeriods',
    'WorkingPeriodSubsystems',
    'WorkingPeriodEmployees',
    'WorkingPeriodForMainOrganization',
    'WorkingPeriodsForMainOrganization',
  ],
  keepUnusedDataFor: CACHE_EXPIRATION_TIME,
  endpoints: builder => ({
    getWorkingPeriodsI: builder.infiniteQuery<
      WorkingPeriodGrid,
      FiltersGrid,
      Pagination
    >({
      infiniteQueryOptions,
      query: ({ queryArg, pageParam }) => ({
        url: `/query`,
        params: { ...queryArg, ...pageParam },
      }),
      providesTags: () => ['WorkingPeriods'],
    }),
    getWorkingPeriodById: builder.query<WorkingPeriod, string>({
      query: id => ({
        url: `/${id}`,
      }),
      providesTags: () => ['WorkingPeriod'],
    }),
    createWorkingPeriod: builder.mutation<
      WorkingPeriod,
      WorkingPeriodRequestBody
    >({
      query: data => ({
        url: '/',
        method: 'POST',
        data,
      }),
      invalidatesTags: result => [
        'WorkingPeriods',
        { type: 'WorkingPeriod', id: result?.id },
      ],
    }),
    updateWorkingPeriod: builder.mutation<
      WorkingPeriod,
      WorkingPeriodRequestBody
    >({
      query: data => ({
        url: `/${data.id}`,
        method: 'PUT',
        data,
      }),
      invalidatesTags: result => [
        'WorkingPeriods',
        { type: 'WorkingPeriod', id: result?.id },
      ],
    }),
    deleteWorkingPeriodById: builder.mutation<Organization, string>({
      query: id => ({
        url: `/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_, __, id) => [
        'WorkingPeriods',
        { type: 'WorkingPeriod', id },
      ],
    }),
    getWorkingPeriodSubsystems: builder.query<
      WorkingPeriodSubsystem,
      FiltersGrid | void
    >({
      query: params => {
        return {
          url: '/subsystems/query',
          params,
        };
      },
      providesTags: () => ['WorkingPeriodSubsystems'],
    }),
    getWorkingPeriodSubsystemsI: builder.infiniteQuery<
      WorkingPeriodSubsystem,
      FiltersGrid,
      Pagination
    >({
      infiniteQueryOptions,
      query: ({ queryArg, pageParam }) => ({
        url: '/subsystems/query',
        params: { ...queryArg, ...pageParam },
      }),
      providesTags: () => ['WorkingPeriodSubsystems'],
    }),
    getWorkingPeriodEmployees: builder.infiniteQuery<
      WorkingPeriodEmployee[],
      FiltersGrid,
      Pagination
    >({
      infiniteQueryOptions: infiniteQueryOptionsFn({ pageSize: 500 }),
      query: ({ queryArg, pageParam }) => ({
        url: '/employees/query',
        params: { ...queryArg, ...pageParam },
      }),
      providesTags: () => ['WorkingPeriodEmployees'],
    }),
    getWorkingPeriodForMainOrganization: builder.query<
      WorkingPeriodGrid[],
      FiltersGrid | void
    >({
      query: params => {
        return {
          url: '/main-organization/query',
          params,
        };
      },
      providesTags: () => ['WorkingPeriodsForMainOrganization'],
      transformResponse: (workingPeriods: WorkingPeriodGrid[]) =>
        workingPeriods?.map(wp => ({
          ...wp,
          isMainOrganizationPeriod: true,
        })),
    }),
    getWorkingPeriodForMainOrganizationById: builder.query<
      WorkingPeriodForMainOrganization,
      string
    >({
      query: id => ({
        url: `/main-organization/${id}`,
      }),
      providesTags: () => ['WorkingPeriodForMainOrganization'],
    }),
  }),
});

export default workingPeriodsApi;

export const {
  useGetWorkingPeriodByIdQuery,
  useCreateWorkingPeriodMutation,
  useUpdateWorkingPeriodMutation,
  useGetWorkingPeriodSubsystemsQuery,
  useGetWorkingPeriodEmployeesInfiniteQuery,
  useDeleteWorkingPeriodByIdMutation,
  useGetWorkingPeriodForMainOrganizationQuery,
  useGetWorkingPeriodForMainOrganizationByIdQuery,
} = workingPeriodsApi;

export const useGetWorkingPeriodsIInfiniteQuery = normalizeInfiniteQueryData(
  workingPeriodsApi.useGetWorkingPeriodsIInfiniteQuery,
);

export const useGetWorkingPeriodSubsystemsIInfiniteQuery =
  normalizeInfiniteQueryData(
    workingPeriodsApi.useGetWorkingPeriodSubsystemsIInfiniteQuery,
  );

export const useGetWorkingPeriodEmployeesQueryInfiniteQuery =
  normalizeInfiniteQueryData(
    workingPeriodsApi.useGetWorkingPeriodEmployeesInfiniteQuery,
  );
